import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table } from 'react-bootstrap';
import { getEmployeeStatement } from './API/EmployeeApi';
import EmployeeSearchAdmin from '../../common/empsearch/EmployeeSearchAdmin';
import { useParams, useHistory } from "react-router-dom";
import Dollars from '../../common/typeFormatting/Dollars';
import DateFmt from '../../common/typeFormatting/Date';

const calculateTimeOfService = (hireDate) => {
  const today = new Date();
  const hire = new Date(hireDate);
  
  let years = today.getFullYear() - hire.getFullYear();
  let months = today.getMonth() - hire.getMonth();
  
  if (months < 0 || (months === 0 && today.getDate() < hire.getDate())) {
    years--;
    months += 12;
  }
  
  if (today.getDate() < hire.getDate()) {
    months--;
    if (months < 0) {
      months = 11;
      years--;
    }
  }
  
  const yearsText = years === 1 ? "Year" : "Years";
  const monthsText = months === 1 ? "Month" : "Months";
  
  if (years === 0) {
    return `${months} ${monthsText}`;
  } else if (months === 0) {
    return `${years} ${yearsText}`;
  } else {
    return `${years} ${yearsText}, ${months} ${monthsText}`;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};

const VoucherDetailRow = ({ voucher }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const currentYear = new Date().getFullYear();

  const isExpiringThisYear = (expirationDate) => {
    if (!expirationDate) return false;
    const expYear = new Date(expirationDate).getFullYear();
    return expYear === currentYear;
  };

  return (
    <>
      <tr 
        onClick={() => setIsExpanded(!isExpanded)} 
        style={{ 
          cursor: 'pointer',
          backgroundColor: isExpiringThisYear(voucher.expirationDate) ? '#fff3cd' : 'inherit'
        }}
      >
        <td style={{ width: '15%' }}><DateFmt>{voucher.issueDate}</DateFmt></td>
        <td style={{ width: '25%' }}>{voucher.groupName}</td>
        <td style={{ width: '15%', textAlign: 'right' }}><Dollars>{voucher.initialAmount}</Dollars></td>
        <td style={{ width: '15%', textAlign: 'right', paddingRight: '2rem' }}><Dollars>{voucher.balance}</Dollars></td>
        <td style={{ width: '25%', paddingLeft: '4rem' }}>
          {voucher.expirationDate ? (
            <DateFmt>{voucher.expirationDate}</DateFmt>
          ) : (
            'Does not expire'
          )}
        </td>
        <td style={{ width: '5%', textAlign: 'center' }}>
          {voucher.transactions?.length > 0 ? (isExpanded ? '▲' : '▼') : ''}
        </td>
      </tr>
      {isExpanded && voucher.transactions?.map(transaction => (
        <tr key={transaction.voucherTranId} style={{ backgroundColor: '#f8f9fa' }}>
          <td style={{ paddingLeft: '2rem', fontSize: 12 }}>
            <DateFmt>{transaction.tranDate}</DateFmt>
          </td>
          <td style={{ fontSize: 12 }}>{transaction.tranTypeId}{transaction.description != null ? " - " + transaction.description : ""}</td>
          <td></td>
          <td className="text-right" style={{ fontSize: 12, paddingRight: '2rem' }}>
            <Dollars>{transaction.amount}</Dollars>
          </td>
          <td colSpan={2}></td>
        </tr>
      ))}
    </>
  );
};

const AdminVoucherDetails = () => {
  const { employeeId } = useParams();
  const history = useHistory();

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [voucherData, setVoucherData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeOfService, setTimeOfService] = useState("");
  const [travelVouchers, setTravelVouchers] = useState([]);
  const [partnerTechVouchers, setPartnerTechVouchers] = useState([]);
  const [travelTotals, setTravelTotals] = useState({ totalBalance: 0, totalBenefitReceived: 0 });
  const [partnerTechTotals, setPartnerTechTotals] = useState({ totalBalance: 0, totalBenefitReceived: 0 });

  useEffect(() => {
    if (employeeId && !selectedEmployee) {
      setSelectedEmployee({
        employeeId: employeeId,
      });
    }
  }, [employeeId, selectedEmployee]);

  useEffect(() => {
    if (!selectedEmployee || !selectedEmployee.employeeId) {
      return;
    }
    
    const loadVoucherData = async () => {
      try {
        const stmt = await getEmployeeStatement(selectedEmployee.employeeId);
        
        // Sort vouchers by issue date descending
        stmt.vouchers.sort((a, b) => new Date(b.issueDate) - new Date(a.issueDate));
        
        // Separate vouchers by allowance type
        const travel = stmt.vouchers.filter(v => v.allowanceTypeId === 'TRAVEL');
        const partnerTech = stmt.vouchers.filter(v => v.allowanceTypeId === 'PARTTECH');
        
        setVoucherData(stmt);
        setTravelVouchers(travel);
        setPartnerTechVouchers(partnerTech);
        
        if (stmt.allowanceSeniorityDate) {
          setTimeOfService(calculateTimeOfService(stmt.allowanceSeniorityDate));
        }
        
        // Calculate totals for each type
        const travelBalance = travel.reduce((sum, v) => sum + v.balance, 0);
        const travelBenefit = travel.reduce((sum, v) => sum + v.initialAmount, 0);
        setTravelTotals({ totalBalance: travelBalance, totalBenefitReceived: travelBenefit });
        
        const techBalance = partnerTech.reduce((sum, v) => sum + v.balance, 0);
        const techBenefit = partnerTech.reduce((sum, v) => sum + v.initialAmount, 0);
        setPartnerTechTotals({ totalBalance: techBalance, totalBenefitReceived: techBenefit });
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading allowance data:', error);
        setIsLoading(false);
      }
    };

    if (selectedEmployee) {
      loadVoucherData();
    }
  }, [selectedEmployee]);

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    history.push(`/travel/employees/${employee.employeeId}`);
  };

  return (
    <>
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Body>
              <Card.Title>Employee Search</Card.Title>
              <Row>
                <Col>
                  <EmployeeSearchAdmin
                    onSelect={handleEmployeeSelect}
                    preselectedEmployee={selectedEmployee} />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {!isLoading && voucherData && (
        <>
          {/* Travel Allowances Card */}
          {travelVouchers.length > 0 && (
            <Row className="mb-4">
              <Col>
                <Card>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <Card.Title>Travel Allowances</Card.Title>
                    </div>
                    
                    <div className="row mb-4">
                      <div className="col-md-4 text-center">
                        <div className="text-muted mb-2">Years of Service at RBA</div>
                        <h4 className="mb-1">{timeOfService}</h4>
                        {voucherData.allowanceSeniorityDate && (
                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                            starting on {formatDate(voucherData.allowanceSeniorityDate)}
                          </div>
                        )}
                      </div>
                      <div className="col-md-4 text-center">
                        <div className="text-muted mb-2">Total Benefit Received</div>
                        <h4><Dollars>{travelTotals.totalBenefitReceived}</Dollars></h4>
                      </div>
                      <div className="col-md-4 text-center">
                        <div className="text-muted mb-2">Available Balance</div>
                        <h4><Dollars>{travelTotals.totalBalance}</Dollars></h4>
                      </div>
                    </div>

                    <Table hover responsive style={{ fontSize: 14 }}>
                      <thead>
                        <tr>
                          <th style={{ width: '15%' }}>Issue Date</th>
                          <th style={{ width: '25%' }}>Description</th>
                          <th style={{ width: '15%', textAlign: 'right' }}>Initial Amount</th>
                          <th style={{ width: '15%', textAlign: 'right', paddingRight: '2rem' }}>Current Balance</th>
                          <th style={{ width: '25%', paddingLeft: '4rem' }}>Expiration Date</th>
                          <th style={{ width: '5%' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {travelVouchers.map(voucher => (
                          <VoucherDetailRow key={voucher.voucherId} voucher={voucher} />
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
          
          {/* Partner Tech Allowances Card */}
          {partnerTechVouchers.length > 0 && (
            <Row>
              <Col>
                <Card>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <Card.Title>Partner Tech Allowances</Card.Title>
                    </div>
                    
                    <div className="row mb-4">
                      <div className="col-md-4 text-center">
                        <div className="text-muted mb-2">Partner Level</div>
                        <h4 className="mb-1">{voucherData.partnerLevel || "N/A"}</h4>
                      </div>
                      <div className="col-md-4 text-center">
                        <div className="text-muted mb-2">Total Benefit Received</div>
                        <h4><Dollars>{partnerTechTotals.totalBenefitReceived}</Dollars></h4>
                      </div>
                      <div className="col-md-4 text-center">
                        <div className="text-muted mb-2">Available Balance</div>
                        <h4><Dollars>{partnerTechTotals.totalBalance}</Dollars></h4>
                      </div>
                    </div>

                    <Table hover responsive style={{ fontSize: 14 }}>
                      <thead>
                        <tr>
                          <th style={{ width: '15%' }}>Issue Date</th>
                          <th style={{ width: '25%' }}>Description</th>
                          <th style={{ width: '15%', textAlign: 'right' }}>Initial Amount</th>
                          <th style={{ width: '15%', textAlign: 'right', paddingRight: '2rem' }}>Current Balance</th>
                          <th style={{ width: '25%', paddingLeft: '4rem' }}>Expiration Date</th>
                          <th style={{ width: '5%' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerTechVouchers.map(voucher => (
                          <VoucherDetailRow key={voucher.voucherId} voucher={voucher} />
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
          
          {/* No Vouchers Message */}
          {travelVouchers.length === 0 && partnerTechVouchers.length === 0 && (
            <Row>
              <Col>
                <Card>
                  <Card.Body className="text-center p-5">
                    <h5>No allowances found for this employee.</h5>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
};

export default AdminVoucherDetails;